import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { useFeatureToggle } from 'platform/utilities/feature-toggles';
import { focusElement, scrollToTop } from 'platform/utilities/ui';
import recordEvent from 'platform/monitoring/record-event';
import { fetchCh31CaseStatusDetails } from '../actions/ch31-case-status-details';
import HubCardList from '../components/HubCardList';
import NeedHelp from '../components/NeedHelp';
import ApplicationDiscontinuedAlert from '../components/ApplicationDiscontinuedAlert';
import LoadCaseDetailsFailedAlert from '../components/LoadCaseDetailsFailedAlert';
import ApplicationInterruptedAlert from '../components/ApplicationInterruptedAlert';
import CaseProgressBar from '../components/CaseProgressBar';
import { getCurrentStepFromStateList } from '../helpers';

const stepLabels = [
  'Application received',
  'Basic eligibility determination',
  'Counselor assignment',
  'Initial counselor meeting',
  'Entitlement determination',
  'Rehabilitation plan development',
  'Rehabilitation plan activated',
];

const pageHeading = 'Your VR&E benefit status';
const pageTitle = 'Your VR&E Benefit Status';
const statusBasePath = '';
// Internal router root. In the browser this resolves to:
// /careers-employment/track-your-vre-benefits/vre-benefit-status
const statusRootRoute = '/';

const toStepSlug = label =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const stepSlugByIndex = [
  null,
  ...stepLabels.map(label => {
    return toStepSlug(label);
  }),
];

const MyCaseManagementHub = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { useToggleLoadingValue, useToggleValue, TOGGLE_NAMES } =
    useFeatureToggle();
  const featureTogglesLoading = useToggleLoadingValue();
  const showMyCaseManagementHubPage = useToggleValue(
    TOGGLE_NAMES.vre_eligibility_status_phase_2_updates,
  );
  const showStep7UpdatedUi = useToggleValue(TOGGLE_NAMES.vreStep7Changes);

  const total = stepLabels.length;
  const [current, setCurrent] = useState(1);

  const caseStatusState = useSelector(state => state?.ch31CaseStatusDetails);

  const loading = caseStatusState?.loading;
  const caseStatusDetails = caseStatusState?.data;
  const caseStatusError = caseStatusState?.error;
  const isLoading = loading || featureTogglesLoading;

  const attrs = caseStatusDetails?.attributes || {};
  const resCaseId = attrs?.resCaseId;
  const isTransferredToCwnrs =
    attrs?.isTransferredToCwnrs === true ||
    caseStatusError?.isTransferredToCwnrs === true;
  const externalStatus = attrs.externalStatus || {};
  const shouldShowLoadCaseDetailsFailedAlert =
    caseStatusError || isTransferredToCwnrs;

  const {
    isDiscontinued = false,
    isInterrupted = false,
    interruptedReason,
    stateList = [],
  } = externalStatus;
  const currentStepNumber = getCurrentStepFromStateList(stateList, total);
  const currentStepCode = stateList?.[currentStepNumber - 1]?.stepCode;

  useEffect(() => {
    if (!isLoading) {
      scrollToTop();
      focusElement('h1');
    }
  }, [isLoading]);

  useEffect(() => {
    const stepTitle = `${pageTitle} | Veterans Affairs`;
    document.title = stepTitle;
  }, []);

  useEffect(() => {
    dispatch(fetchCh31CaseStatusDetails());
  }, [dispatch]);

  useEffect(() => {
    if (!stateList.length) return;

    setCurrent(getCurrentStepFromStateList(stateList, total));
  }, [stateList, total]);

  useEffect(() => {
    if (
      !showMyCaseManagementHubPage ||
      isLoading ||
      caseStatusError ||
      isTransferredToCwnrs ||
      !caseStatusDetails
    ) {
      return;
    }

    if (isDiscontinued || isInterrupted) {
      if (location.pathname !== statusRootRoute) {
        navigate(statusRootRoute, { replace: true });
      }
      return;
    }

    const stepSlug = stepSlugByIndex[current];
    if (!stepSlug) return;

    const targetPath = `${statusBasePath}/${stepSlug}`;
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [
    showMyCaseManagementHubPage,
    isLoading,
    caseStatusError,
    isTransferredToCwnrs,
    caseStatusDetails,
    isDiscontinued,
    isInterrupted,
    current,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (!showMyCaseManagementHubPage || isLoading || !caseStatusDetails) {
      return;
    }

    recordEvent({
      event: 'vre-ch31-benefit-status-view',
      'current-step-number': currentStepNumber,
      'step-code': currentStepCode,
      'is-discontinued': isDiscontinued,
      'is-interrupted': isInterrupted,
      'is-transferred-to-cwinrs': isTransferredToCwnrs,
      'has-opted-for-eva': attrs?.hasVeteranOptedForEva === true,
      'has-appointment-details': Boolean(attrs?.orientationAppointmentDetails),
    });
  }, [
    attrs?.hasVeteranOptedForEva,
    attrs?.orientationAppointmentDetails,
    caseStatusDetails,
    currentStepCode,
    currentStepNumber,
    isDiscontinued,
    isInterrupted,
    isTransferredToCwnrs,
    isLoading,
    showMyCaseManagementHubPage,
  ]);

  if (isLoading) {
    return (
      <div>
        <div className="usa-width-two-thirds vads-u-margin-bottom--4 vads-u-margin-top--0p5 vads-u-margin-x--1 medium-screen:vads-u-margin-x--0 ">
          <h1>{pageHeading}</h1>
          <va-loading-indicator
            set-focus
            message="Loading your case management hub..."
          />
        </div>
      </div>
    );
  }

  if (!showMyCaseManagementHubPage) {
    return (
      <div className="usa-width-two-thirds vads-u-margin-top--0p5 vads-u-margin-x--1 medium-screen:vads-u-margin-x--0">
        <h1>{pageHeading}</h1>
        <p className="vads-u-color--gray-medium">
          This page isn’t available right now.
        </p>
      </div>
    );
  }

  return (
    <div className="usa-width-two-thirds vads-u-margin-top--0p5 vads-u-margin-x--1 medium-screen:vads-u-margin-x--0">
      <h1>{pageHeading}</h1>

      <p>
        The Veteran Readiness and Employment (VR&E) benefits tracker helps you
        stay informed about your Chapter 31 progress. It guides you from the
        moment you submit your application through the stages of your claim.
      </p>

      {shouldShowLoadCaseDetailsFailedAlert && (
        <LoadCaseDetailsFailedAlert
          isTransferredToCwnrs={isTransferredToCwnrs}
        />
      )}
      {!shouldShowLoadCaseDetailsFailedAlert && isDiscontinued && (
        <ApplicationDiscontinuedAlert resCaseId={resCaseId} />
      )}
      {!shouldShowLoadCaseDetailsFailedAlert && isInterrupted && (
        <ApplicationInterruptedAlert interruptedReason={interruptedReason} />
      )}

      {!shouldShowLoadCaseDetailsFailedAlert &&
        !isDiscontinued &&
        !isInterrupted && (
          <>
            <CaseProgressBar
              current={current}
              stepLabels={stepLabels}
              attributes={attrs}
              showStep7UpdatedUi={showStep7UpdatedUi}
            />

            <HubCardList step={current} stateList={stateList} />
          </>
        )}

      <NeedHelp />
      <va-back-to-top />
    </div>
  );
};

export default MyCaseManagementHub;
